import React, { useCallback, useState, useEffect } from "react";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import pxToRem from "~libs/pxToRem";
import { makeStyles } from "@mui/styles";
import { Tooltip } from "@mui/material";
import ItemLoadingCard from "~business/Cards/ItemLoadingCard";
import IconEdit from "~assets/icons/ic_edit.svg";
import useField from "~hooks/useField";
import ListView from "~styled/listView/ListView";
import Next from "@mui/icons-material/ArrowForward";
import filterItems from "~libs/filterItems";
import Search from "~assets/icons/searchGris.png";

export type ListItem = { label: string; value: string | null };

type LocalField = { value: string; valid: boolean; editing: boolean };
type UseFieldReturn = [
  LocalField,
  React.Dispatch<React.SetStateAction<LocalField>>,
  (next: boolean) => void,
  React.RefObject<HTMLInputElement>,
  () => void
];

export interface MultipleItemSelectorCardProps {
  id: string;
  label: string;
  value: string;
  onChange?: (item: ListItem) => void;
  onClick?: () => void;
  placeholder?: string;
  tooltipContent?: React.ReactNode;
  required?: boolean;
  displayError?: boolean;
  focus?: boolean;
  onBlur?: () => void;
  readOnly?: boolean;
  isLoading?: boolean;
  items: ListItem[];
}

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottom: "1px solid #8B8B8B",
    padding: "2%",
  },
  caption: {
    fontFamily: "Nunito",
    fontSize: "16px",
    fontWeight: 600,
    lineHeight: 1.38,
    textAlign: "left",
    color: "#8B8B8B",
  },
  body: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    maxWidth: pxToRem(270),
    width: "50%",
    position: "relative",
  },
  value: {
    fontFamily: "Nunito",
    fontSize: "16px",
    fontWeight: "bold",
    lineHeight: 1.38,
    textAlign: "right",
    color: "#424242",
  },
  input: {
    padding: "5%",
    textAlign: "right",
    width: "100%",
    "& .Mui-error": {
      borderRadius: "4px",
      border: "solid 1.7px #f77221",
    },
    "&:Mui-error": {
      borderRadius: "4px",
      border: "solid 1.7px #f77221",
    },
  },
  buttonLink: {
    display: "flex",
    alignItems: "center",
    color: "grey",
    paddingLeft: 8,
    cursor: "pointer",
  },
  icon: {
    width: 24,
    height: 24,
  },
  listView: {
    position: "absolute",
    top: "85%",
    width: "100%",
    zIndex: 2 as any,
  },
}));

const handleValidate =
  (required?: boolean) =>
  (val: string): boolean =>
    !required || !!val;

const MultipleItemSelectorCard: React.FC<MultipleItemSelectorCardProps> = ({
  id,
  label,
  value,
  onChange,
  onClick,
  placeholder,
  tooltipContent,
  required = false,
  displayError = false,
  focus = false,
  onBlur,
  readOnly = false,
  isLoading = false,
  items,
}) => {
  const classes = useStyles();

  const [filter, setFilter] = useState<string>("");
  const [listView, setListView] = useState<boolean>(false);

  const validateFunction = useCallback(handleValidate(required), [required]);

  const [localValue, setValue, handleEdit, reference] = useField(
    value,
    validateFunction,
    focus,
    isLoading,
    readOnly
  ) as unknown as UseFieldReturn;

  const handleBlur = () => onBlur?.();

  const handleChangeValue = (next: string) => {
    setFilter(next);
    setValue((prev) => ({ ...prev, value: next }));
    setListView(true);
  };

  const handleItem = (item: ListItem) => {
    setFilter("");
    setListView(false);
    setValue((prev) => ({ ...prev, value: item.label }));
    onChange?.(item);
  };

  const handleClickTooltip = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    setListView(true);
  };

  const showError = !localValue.valid && displayError;

  useEffect(() => {
    if (!localValue.editing && localValue.value !== value) {
      setValue((prev) => ({ ...prev, value: value }));
    }
  }, [value, localValue.editing]);

  if (isLoading) return <ItemLoadingCard label={label} />;

  const baseList: ListItem[] =
    filter === "" ? items : (filterItems(items, filter) as ListItem[]);
  const listForView: ListItem[] = baseList.map((it) => ({
    label: it.label ?? "",
    value: it.value ?? null,
  }));

  return (
    <div className={classes.container}>
      <Typography className={classes.caption}>{label}</Typography>
      <div className={classes.body}>
        {localValue.editing ? (
          <>
            <TextField
              id={id}
              style={{ width: "100%" }}
              autoComplete="nope"
              spellCheck="false"
              inputProps={{
                className: classes.input,
                autoComplete: "nope",
                autoCorrect: "off",
                spellCheck: "false",
                form: {
                  autoComplete: "nope",
                },
              }}
              onChange={(e) => handleChangeValue(e.target.value)}
              onBlur={handleBlur}
              value={localValue.value}
              variant="outlined"
              placeholder={placeholder}
              error={showError}
              inputRef={reference}
            />
            <Tooltip title={tooltipContent ?? ""}>
              <div onClick={handleClickTooltip} className={classes.buttonLink}>
                <img
                  style={{ width: 28, height: 28 }}
                  alt="Buscar"
                  src={Search}
                />
              </div>
            </Tooltip>
            {listView && (
              <div className={classes.listView}>
                <ListView
                  isLoading={isLoading}
                  onItemSelected={handleItem}
                  IconMostUsed={Next}
                  list={listForView}
                />
              </div>
            )}
          </>
        ) : (
          <>
            {value ? (
              <Typography className={classes.value}>{value}</Typography>
            ) : (
              <div>&mdash;</div>
            )}
            {!readOnly && (
              <div
                className={classes.buttonLink}
                onClick={() => handleEdit(true)}
              >
                <img alt="" src={IconEdit} className={classes.icon} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MultipleItemSelectorCard;
